// Toasts no padrão Apple / macOS Tahoe (liquid glass) — notificações pequenas,
// translúcidas, no canto superior direito, empilhadas, com auto-dismiss e animação
// na curva Apple. Módulo plano e auto-inicializável: define `window.Toast`.
//
// Importado por application.js, form.js e draw.js (os três bundles com notificações).
// O CSS vive em app/javascript/styles/_apple_toast.scss.

const ICONS = {
  success: '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"/><path d="m8.5 12 2.5 2.5 4.5-5"/></svg>',
  error: '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"/><path d="m15 9-6 6M9 9l6 6"/></svg>',
  info: '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"/><path d="M12 11v5"/><path d="M12 8h.01"/></svg>',
  warning: '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M10.3 4.3 2.5 18a1.7 1.7 0 0 0 1.5 2.5h16a1.7 1.7 0 0 0 1.5-2.5L13.7 4.3a1.7 1.7 0 0 0-3 0z"/><path d="M12 9v4"/><path d="M12 17h.01"/></svg>'
}

const CLOSE_ICON = '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M6 6l12 12M18 6 6 18"/></svg>'

const DEFAULT_DURATION = { success: 4500, info: 4500, warning: 5000, error: 8000 }
const MAX_TOASTS = 4
const TYPES = ['success', 'error', 'info', 'warning']
const LEAVE_FALLBACK_MS = 400

let counter = 0
let containerEl = null
const entries = new Map() // id -> { node, timeout, remaining, start }

function getContainer () {
  if (containerEl && document.body.contains(containerEl)) return containerEl

  containerEl = document.querySelector('[data-toast-root]')

  if (!containerEl) {
    containerEl = document.createElement('div')
    containerEl.className = 'apple-toast-container'
    containerEl.setAttribute('data-toast-root', '')
    containerEl.setAttribute('role', 'region')
    containerEl.setAttribute('aria-label', 'Notifications')

    document.body.appendChild(containerEl)
  }

  return containerEl
}

function dismiss (id) {
  const entry = entries.get(id)

  if (!entry) return

  clearTimeout(entry.timeout)
  entries.delete(id)

  const node = entry.node

  node.classList.add('apple-toast--leave')

  let done = false

  const remove = () => {
    if (done) return
    done = true
    node.remove()
  }

  // Só a transição do PRÓPRIO node encerra a saída — transitionend de filhos
  // (ex.: opacity do botão fechar) borbulha e dispararia a remoção cedo.
  const onEnd = (e) => {
    if (e.target !== node) return

    node.removeEventListener('transitionend', onEnd)
    remove()
  }

  node.addEventListener('transitionend', onEnd)
  setTimeout(remove, LEAVE_FALLBACK_MS)
}

function buildToast (id, message, { type, title, dismissible }) {
  const node = document.createElement('div')

  node.className = `apple-toast apple-toast--${type} apple-toast--enter`
  node.setAttribute('data-toast-id', String(id))
  node.setAttribute('role', type === 'error' ? 'alert' : 'status')
  node.setAttribute('aria-live', type === 'error' ? 'assertive' : 'polite')

  const icon = document.createElement('div')

  icon.className = 'apple-toast__icon'
  icon.setAttribute('aria-hidden', 'true')
  icon.innerHTML = ICONS[type] || ICONS.info

  node.appendChild(icon)

  const body = document.createElement('div')

  body.className = 'apple-toast__body'

  if (title) {
    const titleEl = document.createElement('p')

    titleEl.className = 'apple-toast__title'
    titleEl.textContent = title

    body.appendChild(titleEl)
  }

  const messageEl = document.createElement('p')

  messageEl.className = title ? 'apple-toast__message' : 'apple-toast__message apple-toast__message--lead'
  messageEl.textContent = message

  body.appendChild(messageEl)
  node.appendChild(body)

  if (dismissible) {
    const close = document.createElement('button')

    close.type = 'button'
    close.className = 'apple-toast__close'
    close.setAttribute('aria-label', 'Dismiss')
    close.innerHTML = CLOSE_ICON

    close.addEventListener('click', () => dismiss(id))

    node.appendChild(close)
  }

  return node
}

function show (message, opts = {}) {
  if (message === null || message === undefined || message === '') return null

  const type = TYPES.includes(opts.type) ? opts.type : 'info'
  const dismissible = opts.dismissible !== false
  const duration = opts.duration != null ? opts.duration : (DEFAULT_DURATION[type] || 4500)
  const id = ++counter

  const node = buildToast(id, String(message), { type, title: opts.title, dismissible })
  const container = getContainer()

  container.insertBefore(node, container.firstChild)

  const entry = { node, timeout: null, remaining: duration, start: 0 }

  entries.set(id, entry)

  // Anima a entrada no próximo frame: o 1º frame pinta o estado --enter, o 2º
  // remove a classe para disparar a transição.
  requestAnimationFrame(() => requestAnimationFrame(() => node.classList.remove('apple-toast--enter')))

  // Auto-dismiss com pausa no hover/foco (só quando há duração > 0).
  if (duration) {
    const pause = () => {
      if (!entry.timeout) return

      clearTimeout(entry.timeout)
      entry.timeout = null
      entry.remaining -= performance.now() - entry.start
    }

    const resume = () => {
      if (entry.timeout) return

      // Se o relógio passou do tempo (aba em segundo plano / timer estrangulado),
      // dispensa já em vez de congelar o toast para sempre.
      if (entry.remaining <= 0) {
        dismiss(id)

        return
      }

      entry.start = performance.now()
      entry.timeout = setTimeout(() => dismiss(id), entry.remaining)
    }

    node.addEventListener('mouseenter', pause)
    node.addEventListener('mouseleave', resume)
    node.addEventListener('focusin', pause)
    node.addEventListener('focusout', resume)

    entry.start = performance.now()
    entry.timeout = setTimeout(() => dismiss(id), duration)
  }

  // Limite de toasts simultâneos: remove o mais antigo (ignora os que já saem).
  const live = Array.from(container.children).filter((c) => !c.classList.contains('apple-toast--leave'))

  if (live.length > MAX_TOASTS) {
    dismiss(Number(live[live.length - 1].getAttribute('data-toast-id')))
  }

  return id
}

function clear () {
  entries.forEach((entry) => clearTimeout(entry.timeout))
  entries.clear()

  if (containerEl) containerEl.innerHTML = ''
}

if (!window.Toast) {
  window.Toast = {
    show,
    success: (message, opts = {}) => show(message, { ...opts, type: 'success' }),
    error: (message, opts = {}) => show(message, { ...opts, type: 'error' }),
    info: (message, opts = {}) => show(message, { ...opts, type: 'info' }),
    warning: (message, opts = {}) => show(message, { ...opts, type: 'warning' }),
    dismiss,
    clear
  }

  // Ponte por CustomEvent — permite disparar toasts sem importar o módulo.
  document.addEventListener('toast:show', (e) => show((e.detail || {}).message, e.detail || {}))

  // Turbo: limpa os toasts antes de cachear a página (evita reaparecerem no voltar).
  document.addEventListener('turbo:before-cache', clear)
}
